import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, get, remove, update } from 'firebase/database';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { logAdminAction } from '../../firebase/auditLogger';
import { sendTemplateNotification, createNotification } from '../../services/notificationService';
import { NOTIFICATION_TEMPLATES } from '../../data/notificationTemplates';
import './AdminUsers.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { userProfile } = useAuth();
  const [suspensionModal, setSuspensionModal] = useState({ show: false, user: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersRef = ref(db, 'users');
      const snapshot = await get(usersRef);
      if (snapshot.exists()) {
        const usersObj = snapshot.val();
        const usersList = Object.keys(usersObj).map(key => ({
          uid: key,
          ...usersObj[key]
        }));
        setUsers(usersList.reverse()); // Newest first
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (uid) => {
    if (window.confirm("Are you SURE you want to permanently delete this user document? This action cannot be undone.")) {
      try {
        await remove(ref(db, `users/${uid}`));
        logAdminAction(userProfile?.adminId, "Permanently Deleted User Account and Profile Data", uid);
        fetchUsers();
      } catch (error) {
        alert("Error deleting user: " + error.message);
      }
    }
  };

  const handleToggleSuspend = async (uid, currentStatus, user) => {
    if (currentStatus === 'suspended' || currentStatus === 'blocked') {
      // Direct reactivate
      try {
        await update(ref(db, `users/${uid}`), {
          status: 'active',
          suspensionReason: null,
          suspensionType: null,
          suspensionExpiry: null
        });
        logAdminAction(userProfile?.adminId, "Reactivated user account", uid);
        fetchUsers();
      } catch (err) {
        alert("Failed to reactivate: " + err.message);
      }
    } else {
      setSuspensionModal({ show: true, user });
    }
  };

  const confirmSuspension = async (templateKey) => {
    const { user } = suspensionModal;
    if (!user) return;

    setSubmitting(true);
    try {
      const template = NOTIFICATION_TEMPLATES[templateKey];
      const newStatus = templateKey === 'SUSPENSION_AI_REVIEW' ? 'suspended' : 'suspended'; // Can be adjusted
      
      const updates = {
        status: newStatus,
        suspensionReason: template.title,
        suspensionDetail: template.message.split('\n\n')[1] || template.message, // Extract the core reason description
        suspensionType: templateKey.includes('AI') ? 'AI Block' : 'Automated',
        updatedAt: Date.now()
      };

      await update(ref(db, `users/${user.uid}`), updates);

      // Personalize message
      let message = template.message
        .replace('[User Name]', user.name || 'Developer')
        .replace('[User Email]', user.email || 'N/A');

      await createNotification(user.uid, template.title, message, template.type);

      logAdminAction(userProfile?.adminId, `Suspended user using template: ${templateKey}`, user.uid);
      
      setSuspensionModal({ show: false, user: null });
      fetchUsers();
      alert("User suspended and notified successfully.");
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter based on search query
  const filteredUsers = users.filter(u => 
    (u.name && u.name.toLowerCase().includes(search.toLowerCase())) ||
    (u.email && u.email.toLowerCase().includes(search.toLowerCase())) ||
    (u.profileId && u.profileId.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="animate-up">
      <div className="admin-page-header">
        <h1>Users Management</h1>
        <p>View, suspend, or delete DevTracker accounts.</p>
      </div>

      <div className="admin-card">
        {/* Toolbar */}
        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px'}}>
          <div style={{position: 'relative', width: '300px'}}>
            <i className="fa-solid fa-search" style={{position:'absolute', top:'12px', left:'15px', color:'#8b949e'}}></i>
            <input 
              type="text" 
              placeholder="Search users..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 10px 10px 40px', background: 'rgba(0,0,0,0.2)',
                border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px', fontSize: '0.9rem'
              }}
            />
          </div>
          <button onClick={fetchUsers} className="btn" style={{background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: '8px', padding: '0 15px'}}>
            <i className="fa-solid fa-arrows-rotate"></i> Refresh
          </button>
        </div>

        {/* Data Table */}
        {loading ? (
          <div style={{padding: '40px', textAlign: 'center', color: '#8b949e'}}>Loading users...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name / Email</th>
                  <th>Profile ID</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan="5" style={{textAlign: 'center', padding: '30px', color: '#8b949e'}}>No users found.</td></tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.uid}>
                      <td style={{fontFamily: 'monospace', color: '#8b949e'}}>{user.uid.substring(0, 8)}...</td>
                      <td>
                        <div style={{color: '#fff', fontWeight: '500'}}>{user.name || 'No Name'}</div>
                        <div style={{color: '#8b949e', fontSize: '0.85rem'}}>{user.email} {user.role === 'admin' && <span style={{marginLeft: '5px', color: '#00ffae', border: '1px solid #00ffae', padding: '1px 4px', fontSize: '0.65rem', borderRadius: '4px'}}>ADMIN</span>}</div>
                      </td>
                      <td>
                        {user.profileId ? (
                          <span style={{background: 'rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem'}}>
                            {user.profileId}
                          </span>
                        ) : <span style={{color: '#8b949e'}}>Unassigned</span>}
                      </td>
                      <td>
                        <span className={`status-badge ${(user.status || 'active') === 'active' ? 'status-active' : 'status-banned'}`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <div style={{display: 'flex', gap: '10px'}}>
                          {user.profileId && (
                            <Link to={`/dev/${user.profileId}`} target="_blank" title="View Profile" style={{color: '#a5b4fc', cursor: 'pointer', background: 'rgba(165,180,252,0.1)', padding: '6px 10px', borderRadius: '6px', textDecoration: 'none'}}>
                              <i className="fa-regular fa-eye"></i>
                            </Link>
                          )}
                          <button 
                            onClick={() => handleToggleSuspend(user.uid, user.status, user)} 
                            title={user.status === 'suspended' ? 'Unsuspend' : 'Suspend'} 
                            style={{color: '#ffa116', cursor: 'pointer', background: 'rgba(255,161,22,0.1)', padding: '6px 10px', borderRadius: '6px', border: 'none'}}
                          >
                            <i className="fa-solid fa-ban"></i>
                          </button>
                          <button onClick={() => handleDeleteUser(user.uid)} title="Delete User" style={{color: '#ff375f', cursor: 'pointer', background: 'rgba(255,55,95,0.1)', padding: '6px 10px', borderRadius: '6px', border: 'none'}}>
                            <i className="fa-solid fa-trash-can"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Suspension Modal */}
      {suspensionModal.show && (
        <div className="suspension-modal-overlay">
          <div className="suspension-modal">
            <h3>Suspend User</h3>
            <p>Select a reason to fast-track suspension for <strong>{suspensionModal.user?.name}</strong>.</p>
            
            {suspensionModal.user && (
              <div style={{marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
                <div style={{padding: '12px', background: 'rgba(165,180,252,0.1)', borderRadius: '8px', border: '1px solid rgba(165,180,252,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span style={{fontSize: '0.85rem', color: '#a5b4fc'}}><i className="fa-solid fa-id-badge"></i> Profile ID: {suspensionModal.user.profileId || 'Unassigned'}</span>
                  {suspensionModal.user.profileId && (
                    <Link to={`/dev/${suspensionModal.user.profileId}`} target="_blank" className="btn-link" style={{fontSize: '0.85rem', color: 'var(--brand-accent)', textDecoration: 'none', fontWeight: 'bold'}}>
                      View Profile <i className="fa-solid fa-arrow-up-right-from-square" style={{fontSize: '0.7rem'}}></i>
                    </Link>
                  )}
                </div>
                
                <div style={{padding: '12px', background: 'rgba(0, 255, 174, 0.05)', borderRadius: '8px', border: '1px solid rgba(0, 255, 174, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span style={{fontSize: '0.85rem', color: 'var(--brand-accent)'}}><i className="fa-solid fa-shield-halved"></i> PRIVATE SUPPORT: {suspensionModal.user.suid || 'PENDING INITIALIZATION'}</span>
                  <span style={{fontSize: '0.7rem', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '1px'}}>Verified SUID</span>
                </div>
              </div>
            )}
            
            <div className="template-grid">
              <button className="template-btn" onClick={() => confirmSuspension('SUSPENSION_ID_MISMATCH')} disabled={submitting}>
                <strong>ID Verification Mismatch</strong>
                <span>Registered name and email details appear inconsistent.</span>
              </button>
              <button className="template-btn" onClick={() => confirmSuspension('SUSPENSION_AI_REVIEW')} disabled={submitting}>
                <strong>AI Security Review</strong>
                <span>Detected inconsistencies requiring manual review.</span>
              </button>
              <button className="template-btn" onClick={() => confirmSuspension('SUSPENSION_AUTO_FLAG')} disabled={submitting}>
                <strong>Automated Flag</strong>
                <span>Automatically flagged due to unverifiable identity info.</span>
              </button>
              <button className="template-btn" onClick={() => confirmSuspension('SUSPENSION_DASHBOARD')} disabled={submitting}>
                <strong>Standard Dashboard Alert</strong>
                <span>Concise mismatch alert for immediate dashboard feedback.</span>
              </button>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSuspensionModal({ show: false, user: null })} disabled={submitting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
